import { useInfiniteQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import { PageType } from '@app/components/ChatsContainer/ChatInbox/InboxContent/types';
import { QUERY_JOBS } from '@app/constants/endpoints';
import { QUERY_KEYS } from '@app/constants/service';
import {
  getAccessToken,
  getOktaAccessToken,
  shouldRenewAccessToken,
} from '@app/helpers/localStorage';
import queryClient from '@app/query-client';
import { IN_TESTING_MODE, TEST_AUTH_TOKEN } from '@app/tests/constants';
import { InboxFilters } from '@app/types/reducer-state';

import {
  EXCLUDED_QUERY_PARAMS,
  type ExcludedQueryParam,
} from '../../constants/job-query-filter-params';
import { buildInboxFilterObject, buildPaginatedQueryString } from '../helpers';
import {
  ViewName,
  VIEW_NAMES,
  selectView,
  getViewCacheDefaults,
} from './view-helpers';

interface CasesFilters {
  inboxFilters?: InboxFilters;
  sort?: string;
  order?: string;
  secondarySort?: string;
  secondaryOrder?: string;
  limit?: number;
  search?: string | null;
  jobIds?: string[] | null;
  expandInboxSearchBar?: boolean;
  totalLossStockNumber?: string | null;
  isOpen?: boolean | null;
  view?: ViewName;
  /** Included in inbox cache key so switching modes triggers re-fetch */
  unaddressedEnabled?: boolean;
}

interface CasesRequestBody {
  search?: string;
  jobIds?: string[];
}

interface InboxFilterObject {
  limit: number;
  order: string;
  sort: string;
  secondarySort?: string;
  secondaryOrder?: string;
  onlyActive?: boolean;
  isOpen?: boolean;
  groupIds?: string[];
  operatorIds?: string[];
  secondaryOperatorIds?: string[];
  tags?: string[];
  [key: string]: unknown; // For dynamic refineBy properties
}

type CasesPage = PageType[];
type CasesPages = PageType[][];

/** Fetches cases with view-based routing */
export async function fetchCases(
  filters: CasesFilters,
  pageParam = 1,
): Promise<CasesPage> {
  if (filters.expandInboxSearchBar && !filters.search) {
    return [];
  }

  const view =
    filters.view ||
    selectView({ jobIds: filters.jobIds, search: filters.search });

  const bodyData: CasesRequestBody = {};
  if (filters.search) bodyData.search = filters.search;
  if (filters.jobIds) bodyData.jobIds = filters.jobIds;

  const cacheDefaults = getViewCacheDefaults(view);
  const rawQueryStringParams = { ...cacheDefaults, ...filters, view };

  const queryStringParams = (
    Object.keys(rawQueryStringParams) as Array<
      keyof typeof rawQueryStringParams
    >
  ).reduce((acc, key) => {
    const value = rawQueryStringParams[key];
    if (EXCLUDED_QUERY_PARAMS.includes(key as ExcludedQueryParam)) return acc;
    if (value === null || value === undefined) return acc;
    (acc as Record<string, unknown>)[key] = value;
    return acc;
  }, {} as Partial<CasesFilters>);

  const filterQueryString = buildPaginatedQueryString(
    pageParam,
    queryStringParams,
  );
  const url = `${QUERY_JOBS}${filterQueryString ? `?${filterQueryString}` : ''}`;

  let token = TEST_AUTH_TOKEN;
  if (!IN_TESTING_MODE) {
    token = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  return axios({
    url,
    method: 'POST',
    headers: { Authorization: token },
    data: bodyData,
  }).then((response: AxiosResponse) => response.data);
}

const buildCasesQuery = (
  customFilters: CasesFilters,
  authId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) => {
  const filters = buildInboxFilterObject(
    customFilters,
    authId,
  ) as InboxFilterObject;

  // Determine view and get cache defaults for query key.
  // This ensures live update filter matching works correctly (e.g., isOpen for inbox view).
  const view =
    customFilters.view ||
    selectView({ jobIds: customFilters.jobIds, search: customFilters.search });
  const cacheDefaults = getViewCacheDefaults(view);

  const queryKeyData = {
    ...cacheDefaults,
    ...filters,
    ...(customFilters.search && { search: customFilters.search }),
    ...(customFilters.jobIds && { jobIds: customFilters.jobIds }),
    ...(customFilters.totalLossStockNumber && {
      totalLossStockNumber: customFilters.totalLossStockNumber,
    }),
    ...(customFilters.isOpen !== undefined && {
      isOpen: customFilters.isOpen,
    }),
    ...(customFilters.view && { view: customFilters.view }),
    ...(customFilters.expandInboxSearchBar && {
      expandInboxSearchBar: customFilters.expandInboxSearchBar,
    }),
    // Inbox-only: invalidate cache when switching between unread/unaddressed modes.
    ...(view === VIEW_NAMES.INBOX &&
      customFilters.unaddressedEnabled !== undefined && {
        unaddressedEnabled: customFilters.unaddressedEnabled,
      }),
  };

  return {
    queryKey: [QUERY_KEYS.CASES, queryKeyData],
    queryFn: ({ pageParam = 1 }) =>
      fetchCases(
        {
          ...filters,
          ...(customFilters.search && { search: customFilters.search }),
          ...(customFilters.jobIds && { jobIds: customFilters.jobIds }),
          ...(customFilters.totalLossStockNumber && {
            totalLossStockNumber: customFilters.totalLossStockNumber,
          }),
          ...(customFilters.expandInboxSearchBar && {
            expandInboxSearchBar: customFilters.expandInboxSearchBar,
          }),
          ...(customFilters.view && { view: customFilters.view }),
        },
        pageParam,
      ),
    getNextPageParam: (lastPage: CasesPage, pages: CasesPages) => {
      if ((lastPage?.length || 0) < filters.limit) return undefined;
      return (pages?.length || 0) + 1;
    },
    enabled: options?.enabled,
    staleTime: options?.staleTime,
  };
};

export const useCasesQuery = (
  customFilters: CasesFilters,
  authId: string,
  options?: {
    enabled?: boolean;
  },
) => useInfiniteQuery(buildCasesQuery(customFilters, authId, options));

export const prefetchCasesQuery = (
  customFilters: CasesFilters,
  authId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) =>
  queryClient.prefetchInfiniteQuery(
    buildCasesQuery(customFilters, authId, options),
  );
